import { type CreatePackageDayRequest, type PackageDayOut } from '@utazzo/shared';
import { UzCombobox, UzForm, UzFormField, UzInput, UzNumberInput, UzSwitch } from '@utazzo/ui';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { PackageFormPanel } from './PackageFormPanel';

import { MealPlanPicker } from '@/components/pickers/MealPlanPicker';
import { useCitiesList } from '@/features/cities/use-cities';
import { useDestinationsList } from '@/features/destinations/use-destinations';
import { useMealPlansList } from '@/features/meal-plans/use-meal-plans';
import { RichTextEditor } from '@/features/website-cms/components/RichTextEditor';

interface FormValues {
  dayIndex: number;
  cityId: string | null;
  town: string | null;
  title: string | null;
  subtitle: string | null;
  mealPlanId: string | null;
  briefHtml: string | null;
}

interface Props {
  mode: 'create' | 'edit';
  item?: PackageDayOut;
  nextIndex: number;
  pending: boolean;
  /** The package's selected destination - the day's city list is filtered to its country/state. */
  destinationId?: string | null;
  onClose: () => void;
  /**
   * Live (API) submit. Used when {@link onSubmitDraft} is not provided.
   * Awaited; the editor closes the drawer once the mutation resolves.
   */
  onSubmit?: (payload: CreatePackageDayRequest) => Promise<void>;
  /**
   * Controlled/draft submit. When provided, takes precedence over
   * {@link onSubmit}: the value is returned to the editor (which stages it in
   * memory) instead of hitting the API. No await - the editor handles closing.
   */
  onSubmitDraft?: (day: CreatePackageDayRequest) => void;
}

const matchMealPlanId = (
  mealPlan: string | null | undefined,
  mealPlans: ReadonlyArray<{ id: string; code: string; name: string }>,
): string | null => {
  const text = mealPlan?.trim();
  if (!text) return null;
  const key = text.toLowerCase();
  return (
    mealPlans.find((p) => p.name.toLowerCase() === key || p.code.toLowerCase() === key)?.id ?? null
  );
};

const toDefaults = (
  mode: 'create' | 'edit',
  item: PackageDayOut | undefined,
  nextIndex: number,
  mealPlans: ReadonlyArray<{ id: string; code: string; name: string }>,
): FormValues =>
  mode === 'edit' && item
    ? {
        dayIndex: item.dayIndex,
        cityId: item.cityId,
        town: item.town,
        title: item.title,
        subtitle: item.subtitle,
        mealPlanId: matchMealPlanId(item.mealPlan, mealPlans),
        briefHtml: item.briefHtml,
      }
    : {
        dayIndex: nextIndex,
        cityId: null,
        town: null,
        title: null,
        subtitle: null,
        mealPlanId: null,
        briefHtml: null,
      };

const trimOrNull = (v: string | null | undefined): string | null => {
  if (v === null || v === undefined) return null;
  const t = v.trim();
  return t === '' ? null : t;
};

export const PackageDayFormDrawer = ({
  mode,
  item,
  nextIndex,
  pending,
  destinationId,
  onClose,
  onSubmit,
  onSubmitDraft,
}: Props): React.JSX.Element => {
  const mealPlansQuery = useMealPlansList({
    page: 1,
    pageSize: 200,
    sortBy: 'sortOrder',
    sortDir: 'asc',
    isActive: true,
  });
  const mealPlans = mealPlansQuery.data?.data ?? [];

  const form = useForm<FormValues>({
    defaultValues: toDefaults(mode, item, nextIndex, mealPlans),
  });
  // The "use a city instead" toggle starts OFF in domestic mode - the free-text
  // Town field is the default; the operator flips it on to pick a city instead.
  const [useCity, setUseCity] = useState<boolean>(false);
  // City search term for international destinations (server-side search of the
  // whole country). UzCombobox debounces before firing onSearchChange.
  const [citySearch, setCitySearch] = useState('');

  useEffect(() => {
    form.reset(toDefaults(mode, item, nextIndex, mealPlans));
    setUseCity(false);
    setCitySearch('');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on mode / row change only
  }, [mode, item, nextIndex, form]);

  // Meal plans load async — hydrate the picker when editing a day with a saved plan.
  useEffect(() => {
    if (mode !== 'edit' || !item?.mealPlan || mealPlans.length === 0) return;
    const id = matchMealPlanId(item.mealPlan, mealPlans);
    // Match both null and undefined - RHF default is undefined, edit-mode
    // rehydrate can leave it null. Explicit compare over `!=` keeps eqeqeq happy.
    const existing = form.getValues('mealPlanId');
    if (id && (existing === null || existing === undefined)) {
      form.setValue('mealPlanId', id, { shouldDirty: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable form methods
  }, [mode, item?.mealPlan, mealPlans]);

  // Resolve the package destination's country / state, then list its cities.
  const destinationsQuery = useDestinationsList({
    page: 1,
    pageSize: 200,
    sortBy: 'name',
    sortDir: 'asc',
    isActive: true,
  });
  const selectedDestination = destinationId
    ? (destinationsQuery.data?.data ?? []).find((d) => d.id === destinationId)
    : undefined;
  const countryId = selectedDestination?.country?.id;
  const stateId = selectedDestination?.state?.id;
  // International = the destination's country is anything other than India. For
  // those, the city list spans the whole country (searched server-side, 30 at a
  // time) and the free-text Town field is hidden - the city picker is the only option.
  const countryName = (selectedDestination?.country?.name ?? '').trim().toLowerCase();
  // International only when the destination resolves to a real, non-India country.
  // A destination with no country falls back to domestic so the free-text Town
  // field stays available (the city picker still needs a country to list cities).
  const isInternational = countryName !== '' && countryName !== 'india';

  const citySearchTrim = citySearch.trim();
  const citiesQuery = useCitiesList({
    page: 1,
    pageSize: isInternational ? 30 : 300,
    sortBy: 'name',
    sortDir: 'asc',
    isActive: true,
    ...(countryId ? { countryId } : {}),
    // Domestic narrows to the destination's state; international searches the
    // whole country, fetching matches server-side as the operator types.
    ...(!isInternational && stateId ? { stateId } : {}),
    ...(isInternational && citySearchTrim.length > 0
      ? { search: citySearchTrim, searchMode: 'prefix' as const }
      : {}),
  });
  const cityOptions = useMemo(() => {
    const opts = countryId
      ? [
          { value: '', label: '- None -' },
          ...(citiesQuery.data?.data ?? []).map((c) => ({ value: c.id, label: c.name })),
        ]
      : [{ value: '', label: '- None -' }];
    // Keep the day's existing city visible even when it falls outside the
    // current 30 / search results (international server-search mode).
    if (item?.cityId && item.cityName && !opts.some((o) => o.value === item.cityId)) {
      opts.push({ value: item.cityId, label: item.cityName });
    }
    return opts;
  }, [citiesQuery.data, countryId, item]);

  const submit = form.handleSubmit(async (values) => {
    const mealPlanRow = mealPlans.find((p) => p.id === values.mealPlanId);
    const payload: CreatePackageDayRequest = {
      dayIndex: values.dayIndex,
      // Town is always saved (domestic). The "use a city instead" toggle adds a
      // City-master link on top; international has no town (city only).
      cityId: isInternational || useCity ? values.cityId : null,
      town: isInternational ? null : trimOrNull(values.town),
      title: trimOrNull(values.title),
      subtitle: trimOrNull(values.subtitle),
      mealPlan: mealPlanRow?.name ?? null,
      briefHtml: trimOrNull(values.briefHtml),
    };

    if (onSubmitDraft) {
      onSubmitDraft(payload);
      return;
    }
    await onSubmit?.(payload);
  });

  // Single city picker, placed differently per mode (grid cell when international,
  // the toggle box when domestic). International drives results from the server
  // via onSearchChange (debounced inside UzCombobox).
  const cityField = (
    <UzFormField name="cityId">
      {({ field, error }) => (
        <UzCombobox
          name={field.name}
          label="City"
          placeholder={countryId ? 'Select a city…' : 'Pick a destination first'}
          searchPlaceholder="Search cities…"
          value={(field.value as string | null) ?? ''}
          onValueChange={(v) => field.onChange(v === '' ? null : v)}
          options={cityOptions}
          loading={citiesQuery.isLoading}
          onSearchChange={isInternational ? setCitySearch : undefined}
          hint={
            !destinationId
              ? 'Select the package’s destination on the Details tab first'
              : !countryId
                ? 'This destination has no country set - add one under Masters → Destinations to enable city search'
                : isInternational
                  ? 'Search cities in the destination’s country'
                  : useCity
                    ? 'Cities under the package destination'
                    : 'Toggle “Use a city instead” to pick from the master list'
          }
          error={error}
          disabled={pending || (!isInternational && !useCity) || !countryId}
        />
      )}
    </UzFormField>
  );

  return (
    <PackageFormPanel
      title={mode === 'create' ? 'Add day' : `Edit day ${item?.dayIndex ?? ''}`}
      description="Day number, city, meals and brief for one day of the package."
      pending={pending}
      submitLabel={mode === 'create' ? 'Add day' : 'Save changes'}
      onClose={onClose}
      onSubmit={submit}
    >
      <UzForm form={form} onSubmit={submit}>
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <UzFormField name="dayIndex">
              {({ field, error }) => (
                <UzNumberInput
                  name={field.name}
                  label="Day number"
                  precision={0}
                  min={1}
                  max={365}
                  value={
                    field.value === null || field.value === undefined ? 1 : (field.value as number)
                  }
                  onValueChange={(v) => field.onChange(v ?? 1)}
                  hint="Order in the trip"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            {/* Domestic shows the free-text Town here; international has no town
                (the city picker is the only option, rendered in the grid). */}
            {isInternational ? (
              cityField
            ) : (
              <UzFormField name="town">
                {({ field, error }) => (
                  <UzInput
                    {...field}
                    value={(field.value as string | null) ?? ''}
                    onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                    label="Town"
                    placeholder="e.g. Sonmarg"
                    hint="Optional free-text town"
                    error={error}
                    disabled={pending}
                  />
                )}
              </UzFormField>
            )}
          </div>

          {!isInternational && (
            <div className="space-y-3 rounded-md border border-zinc-200 bg-zinc-50/40 p-3">
              <UzSwitch
                label="Use a city instead"
                description="Pick a city from the package destination instead of a free-text town."
                checked={useCity}
                onCheckedChange={setUseCity}
                disabled={pending}
              />
              {cityField}
            </div>
          )}

          <UzFormField name="title">
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string | null | undefined) ?? ''}
                onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                label="Title"
                placeholder="e.g. Arrival & Goa beaches"
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>

          <UzFormField name="subtitle">
            {({ field, error }) => (
              <UzInput
                {...field}
                value={(field.value as string | null | undefined) ?? ''}
                onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                label="Subtitle"
                placeholder="Short tagline for the day"
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>

          <UzFormField name="mealPlanId">
            {({ field, error }) => (
              <MealPlanPicker
                value={(field.value as string | null) ?? null}
                onValueChange={field.onChange}
                hint="Board basis from the Meal Plans master."
                error={error}
                disabled={pending}
              />
            )}
          </UzFormField>

          <UzFormField name="briefHtml">
            {({ field }) => (
              <RichTextEditor
                value={(field.value as string | null | undefined) ?? ''}
                onChange={(html) => field.onChange(html === '' ? null : html)}
                label="Day brief"
                placeholder="What the traveller does on this day."
                folder="packages"
                stickyTop="flush"
                disabled={pending}
              />
            )}
          </UzFormField>
        </div>
      </UzForm>
    </PackageFormPanel>
  );
};
